import React, { useState, useEffect } from 'react';
import {
  FaHeart, FaComment, FaEye, FaPlus, FaEdit, FaTrash,
  FaSearch, FaCheck, FaGlobe, FaBuilding, FaUser, FaCalendarAlt
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { getAssetUrl } from '../../../utils/apiBase';
import { useAuth } from '../../../contexts/AuthContext';
import { encodeForumId } from '../../../utils/urlEncryption';
import { Modal, Row, Col, Form, Button, Spinner } from 'react-bootstrap';

const ForumList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [forums, setForums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingForum, setEditingForum] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [forumToDelete, setForumToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Restore loading
  const [restoreLoading, setRestoreLoading] = useState({});

  // Form
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    org_id: '',
    attachment: null
  });
  const [formErrors, setFormErrors] = useState({});

  // Track broken images safely
  const [brokenImages, setBrokenImages] = useState(new Set());

  useEffect(() => {
    fetchForums();
  }, [filterStatus]);

  const fetchForums = async () => {
    try {
      setLoading(true);
      const res = await api.forums.getAll({ filter: filterStatus });
      if (res.data.success) {
        setForums(res.data.data.forums || []);
      }
    } catch (err) {
      toast.error('Failed to load forums');
    } finally {
      setLoading(false);
    }
  };

  // Search & Filter
  const filteredForums = forums.filter(forum => {
    const term = searchTerm.toLowerCase();
    const text = `${forum.title} ${forum.description} ${forum.creator?.first_name} ${forum.creator?.last_name}`.toLowerCase();
    return text.includes(term);
  });

  const formatDate = (date) => date ? new Date(date).toLocaleDateString() : '-';

  const canEditForum = (forum) => {
    return user.role === 0 ||
           (user.role === 2 && (forum.created_by === user.id || forum.org_id === user.organization_id));
  };

  // Modal Handlers
  const openModal = (forum = null) => {
    setEditingForum(forum);
    setFormData({
      title: forum?.title || '',
      description: forum?.description || '',
      org_id: forum?.org_id || '',
      attachment: null
    });
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingForum(null);
    setFormData({ title: '', description: '', org_id: '', attachment: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.title.trim()) errors.title = 'Title is required';
    if (!formData.description.trim()) errors.description = 'Description is required';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setModalLoading(true);
      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      if (formData.org_id) data.append('org_id', formData.org_id);
      if (formData.attachment) data.append('attachment', formData.attachment);

      if (editingForum) {
        await api.forums.update(editingForum.id, data);
        toast.success('Forum updated');
      } else {
        await api.forums.create(data);
        toast.success('Forum created');
      }

      fetchForums();
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save forum');
    } finally {
      setModalLoading(false);
    }
  };

  const confirmDelete = (forum) => {
    setForumToDelete(forum);
    setShowDeleteModal(true);
  };

  const deleteForum = async () => {
    try {
      setDeleteLoading(true);
      await api.forums.delete(forumToDelete.id);
      toast.success('Forum deleted');
      fetchForums();
      setShowDeleteModal(false);
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setForumToDelete(null);
    setDeleteLoading(false);
  };

  const restoreForum = async (forum) => {
    try {
      setRestoreLoading(prev => ({ ...prev, [forum.id]: true }));
      await api.forums.restore(forum.id);
      toast.success('Forum restored');
      fetchForums();
    } catch (err) {
      toast.error('Restore failed');
    } finally {
      setRestoreLoading(prev => ({ ...prev, [forum.id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div class="d-flex justify-content-between align-items-center mb-0 flex-wrap gap-2">
          <div className="mb-3 fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
            Manage Forums
          </div>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="mb-3 g-2 sm:g-3 row">
        <div className="mb-2 mb-md-0 col-md-6 col-12">
          <div class="shadow-sm rounded-lg sm:rounded-xl overflow-hidden input-group">
          <span class="bg-white border-0 text-purple-600 px-3 input-group-text"><FaSearch /></span>
          <input
            type="text"
            placeholder="Search by title, description or author..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base form-control"
          />
          </div>
        </div>
        <div class="col-md-3 col-sm-6 col-12">
            <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select"
          >
              <option value="all">All Active</option>
              <option value="global">Global Forums</option>
              <option value="organization">Organization Forums</option>
              <option value="deleted">Deleted Forums</option>
          </select>
        </div>
           <div class="col-md-3 col-sm-6 col-12">
             <button
        bsPrefix="custom-hamburger"
          onClick={() => openModal()}
          className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
        >
          <FaPlus className="w-5 h-5" /> Add Forum
        </button>
          </div>
      </div>

      {/* Forums Grid */}
      {filteredForums.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl shadow-xl">
          <FaComment className="w-24 h-24 mx-auto text-purple-300 mb-4" />
          <p className="text-xl font-semibold text-purple-700">
            {searchTerm || filterStatus !== 'all' ? 'No forums found' : 'No forums yet'}
          </p>
          <p className="text-gray-600 mt-2">
            {searchTerm || filterStatus !== 'all'
              ? 'Try changing your search or filter'
              : 'Click "Add Forum" to create the first one'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
          {filteredForums.map((forum) => (
            <div
              key={forum.id}
              className={`bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 ${forum.deleted ? 'opacity-75' : ''}`}
            >
              {/* Image Section - 100% Safe */}
              <div className="relative h-48 bg-gradient-to-br from-purple-100 to-pink-100">
                {forum.attachment && !brokenImages.has(forum.id) ? (
                  <img
                    src={getAssetUrl(forum.attachment)}
                    alt={forum.title}
                    className="w-full h-full object-cover"
                    onError={() => setBrokenImages(prev => new Set(prev).add(forum.id))}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-purple-600">
                    <FaComment className="w-16 h-16 mb-3" />
                    <span className="font-semibold">No Image</span>
                  </div>
                )}

                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold text-white shadow-md ${forum.org_id ? 'bg-blue-600' : 'bg-green-600'}`}>
                    {forum.org_id ? <FaBuilding className="inline mr-1" /> : <FaGlobe className="inline mr-1" />}
                    {forum.org_id ? 'Organization' : 'Global'}
                  </span>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4">
                <h3 className="font-bold text-lg text-gray-800 line-clamp-2 mb-2">{forum.title}</h3>
                <p className="text-gray-600 text-sm line-clamp-3 mb-4">{forum.description || 'No description'}</p>

                <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
                  <div className="flex items-center gap-1">
                    <FaUser className="text-purple-500" />
                    <span>{forum.creator?.first_name} {forum.creator?.last_name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <FaCalendarAlt className="text-purple-400" />
                    <span>{formatDate(forum.createdAt)}</span>
                  </div>
                </div>

                <div className="flex gap-4 text-sm mb-4">
                  <span className="flex items-center gap-1 text-red-600"><FaHeart /> {forum.likes_count || 0}</span>
                  <span className="flex items-center gap-1 text-blue-600"><FaComment /> {forum.comments_count || 0}</span>
                  <span className="flex items-center gap-1 text-gray-600"><FaEye /> {forum.views_count || 0}</span>
                </div>

                <div className="d-flex gap-1 justify-content-center">
                  <Button
                    bsPrefix="custom-hamburger"
                    onClick={() => navigate(`/admin/forums/${encodeForumId(forum.id)}`)}
                    title="View Forum"
                    className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded p-1"
                  >
                    <FaEye className="w-5 h-5" />
                  </Button>

                  {forum.deleted ? (
                    <Button
                      bsPrefix="custom-hamburger"
                      onClick={() => restoreForum(forum)}
                      disabled={restoreLoading[forum.id]}
                      title="Restore Forum"
                      className="border-green-300 text-green-600 hover:bg-green-50 rounded p-1"
                    >
                      <FaCheck className="w-5 h-5" />
                    </Button>
                  ) : canEditForum(forum) && (
                    <>
                      <Button
                        bsPrefix="custom-hamburger"
                        onClick={() => openModal(forum)}
                        title="Edit Forum"
                        className="border-purple-300 text-purple-600 hover:bg-purple-100 rounded p-1"
                      >
                        <FaEdit className="w-5 h-5" />
                      </Button>
                      <Button
                        bsPrefix="custom-hamburger"
                        onClick={() => confirmDelete(forum)}
                        title="Delete Forum"
                        className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1"
                      >
                        <FaTrash className="w-5 h-5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal (UI structure matched to UserManagement.jsx) */}
      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        fullscreen="sm-down"
        backdrop="static"
      >
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:py-4 sm:px-4">
          <Modal.Title className="font-bold text-base sm:text-lg">
            {editingForum ? `Edit Forum: ${editingForum.title || ''}` : 'Create New Forum'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-0 sm:p-5 bg-gray-50">
          <Form>
            <Row className="mb-3 g-2 sm:g-3">
              <Col xs={12} md={12}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Title <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    name="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    isInvalid={!!formErrors.title}
                    placeholder="Enter forum title"
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.title}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Description <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    isInvalid={!!formErrors.description}
                    placeholder="Write a helpful description for this forum..."
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.description}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {user.role === 0 && (
              <Row className="mb-3">
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Organization (Optional)</Form.Label>
                    <Form.Select
                      name="org_id"
                      value={formData.org_id}
                      onChange={(e) => setFormData({ ...formData, org_id: e.target.value })}
                      className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                    >
                      <option value="">Global (Visible to all)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Attachment (Image)</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFormData({ ...formData, attachment: e.target.files[0] || null })}
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer className="bg-gray-100 border-0 p-3 sm:p-4">
          <Row className="w-100 g-2">
            <Col xs={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={closeModal}
                disabled={modalLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleSubmit}
                disabled={modalLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {modalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {editingForum ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingForum ? 'Update' : 'Create'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation (match UserManagement.jsx modal styling) */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered fullscreen="sm-down">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 px-4 py-3 sm:px-4 sm:py-4">
          <Modal.Title className="text-sm sm:text-base">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="mb-3 flex justify-center items-center">
            <FaTrash size={50} className="text-gray-400" />
          </div>
          <h5 className="font-bold text-gray-800 text-sm sm:text-base">Delete Forum</h5>
          <p className="text-muted text-xs sm:text-sm">
            Are you sure you want to delete{' '}
            <strong>{forumToDelete ? forumToDelete.title : ''}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 p-3">
          <Row className="w-100 g-2">
            <Col xs={6}>
              <Button 
                bsPrefix="custom-hamburger"
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col xs={6}>
              <Button 
                bsPrefix="custom-hamburger"
                onClick={deleteForum}
                disabled={deleteLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Forum'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ForumList;